import { ReactNode } from "react";
import { FatigueSettings } from "./screens/creatives/types";
import { RemixiconComponentType } from "@remixicon/react";
import { UserRole } from "./screens/settings/types";

export enum CurrentNodeType {
  AGENT = "AGENT",
  USER = "USER",
}

export interface Asset {
  asset_id: string;
  asset_name: string | null;
  status: AssetStatus;
}

export interface Paginator {
  current_page: number;
  page_size: number;
  total_pages: number;
  total_count: number;
}

export interface Paginator {
  current_page: number;
  page_size: number;
  total_pages: number;
  total_count: number;
}

export enum AssetStatus {
  // Facebook statuses
  ACTIVE = "ACTIVE",
  PAUSED = "PAUSED",
  DELETED = "DELETED",
  ARCHIVED = "ARCHIVED",

  // Google statuses
  ENABLED = "ENABLED",
  REMOVED = "REMOVED",
  UNKNOWN = "UNKNOWN",
  UNSPECIFIED = "UNSPECIFIED",
}

export enum AccountStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  NOT_CONNECTED = "NOT_CONNECTED",
  EXPIRED = "EXPIRED",
}

export enum PlatformType {
  GOOGLE = "GOOGLE",
  FACEBOOK = "FACEBOOK",
  TIKTOK = "TIKTOK",
  LINKEDIN = "LINKEDIN",
}

export enum TimeIntervals {
  "1Day" = "1 Day",
  "3Day" = "3 Day",
  "7Day" = "7 Day",
  "28Day" = "28 Day",
  "1Week" = "1 Week",
}

export type CreativeType = "video" | "image" | "mixed" | null;

export type AccountDefaultDateKey =
  | "last_7_days"
  | "last_14_days"
  | "last_28_days"
  | "last_30_days";

export interface Account {
  id: string;
  ad_account_id?: string;
  platform: PlatformType;
  type?: string;
  name: string;
  currency?: string;
  time_zone?: string;
  platform_creation_time_stamp?: string;
  child_accounts?: Account[];
  additional_info?: {
    ad_account_icon_url?: string;
    winning_filters?: Array<{
      criterias: Array<{
        conditions: Array<{
          operator: string;
          type: "static" | "dynamic";
          value: number | string;
          metric_id: string;
        }>;
        criteria_name: string;
      }>;
      target_campaigns: string[];
    }>;
    metric_ids?: string[];
    default_filters?: SectionFilter[];
    attribution_windows?: string[];
    default_date?: AccountDefaultDateKey;
    fatigue_settings?: FatigueSettings;
    description?: string;
    instructions?: string;
    ad_information?: {
      account_icon_url?: string;
      aspect_ratio?: string;
      avoid_danger_zone?: boolean;
    };
  };
  creation_time_stamp?: string;
  status?: (typeof AccountStatus)[keyof typeof AccountStatus];
  default?: boolean;
  is_demo?: boolean;
  labelPrefixIcon?: string | RemixiconComponentType;
  statusIcon?: ReactNode;
  logos?: {
    file_id: string;
    type?: string;
    url: string;
  }[];
  role?: UserRole;
  version?: "v1" | "v2";
}

export enum MetricType {
  calculated = "CALCULATED",
  primary = "PRIMARY",
  custom = "CUSTOM",
}

export interface Metric {
  metric_id: string;
  metric_name: string;
  metric_type: MetricType;
  metric_formula: {
    formula: string;
    api_field?: string;
    primary_metric_ids: string[];
  };
  metric_breakdown?: any;
  platform: PlatformType;
  metric_sub_type?: string;
  prefix?: string;
  suffix?: string;
}

export type GraphInterval = "daily" | "weekly" | "monthly" | "three_day";

export interface ConfigurationDropdown {
  key: string;
  label: string;
  type: "single_select" | "multi_select";
  options: {
    key: string;
    label: string;
    info?: string;
    breakdown_by?: string | null;
    creative_type?: CreativeType;
    supporting_columns?: unknown[];
    profile_image_url?: string | null;
    column_type?: "text" | "number" | "text_ai_label" | "text_ai_description";
    label_type?: string;
    show_platforms?: boolean;
    platforms?: PlatformType[];
    supported_breakdowns?: string[];
  }[];
  default?: string | null;
  depends_on?: Array<{
    key: string;
    field: string;
    value: string;
  }>;
  logic?: "all" | "any";
}

export enum FilterOn {
  ADS = "ads",
  GROUPS = "groups",
  CREATIVES = "creatives",
}

export interface DataConfigurationsFilter {
  filter_types: Array<{
    type: "string" | "number" | "number_equals_only" | "option" | "date" | "boolean";
    operators: Array<{
      key: string;
      label: string;
    }>;
  }>;
  filter_choices: Array<{
    key: string;
    label: string;
    field: string;
    filter_type: "string" | "number" | "option";
    type?: "single_select" | "multi_select";
    creative_type?: CreativeType;
    platforms?: PlatformType[];
    show_platforms?: boolean;
    filter_on?: FilterOn[];
    options?: Array<{
      key: string;
      label: string;
      platform?: PlatformType;
    }>;
    children?: Array<{
      key: string;
      label: string;
      filter_type: "string" | "number" | "option";
      type?: "single_select" | "multi_select";
      creative_type?: CreativeType;
      sub_type?: string;
      platforms?: PlatformType[];
      show_platforms?: boolean;
      options?: Array<{
        key: string;
        label: string;
      }>;
    }>;
  }>;
}

export type MetricFormula = {
  api_field?: string;
  formula?: string;
  primary_metric_ids?: string[];
};

export type ColumnOption = {
  key: string;
  label: string;
  creative_type?: CreativeType;
  info?: string;
  sub_type?: string;
  show_platforms?: string;
  platforms?: PlatformType[];
  suffix?: string;
  prefix?: string;
  metric_formula?: MetricFormula;
  supported_views?: string[];
};

export type ColumnChoice = {
  key: string;
  label: string;
  default?: string[];
  column_type: "text" | "number" | "text_ai_label" | "text_ai_description";
  options: ColumnOption[];
};

export interface DataConfigurations {
  dropdowns?: ConfigurationDropdown[];
  filter: DataConfigurationsFilter;
  column_choices: ColumnChoice[];
  section_types: {
    [key: string]: {
      enabled_keys: string[];
      label: string | null;
    };
  };
}

export interface LibraryMedia {
  id: string;
  file_id: string;
  file_name: string;
  file_type: string;
  file_url: string;
  created_at: string;
  updated_at: string;
}

export type File = {
  id: string;
  file_name: string;
  public_url: string;
};

export enum TimeInterval {
  TODAY = "Today",
  YESTERDAY = "Yesterday",
  THIS_WEEK = "This week",
  LAST_WEEK = "Last week",
  LAST_7_DAYS = "Last 7 days",
  LAST_14_DAYS = "Last 14 days",
  LAST_28_DAYS = "Last 28 days",
  LAST_30_DAYS = "Last 30 days",
  LAST_90_DAYS = "Last 90 days",
  THIS_MONTH = "This month",
  LAST_MONTH = "Last month",
  LAST_3_MONTHS = "Last 3 months",
  LAST_12_MONTHS = "Last 12 months",
  CUSTOM = "Custom",
  AUTO = "Auto Select",
}

export interface ReportDate {
  start_date: string;
  end_date: string;
  label?: TimeInterval;
}

export interface CreativeReportMetadata {
  date?: ReportDate;
  compare_date?: ReportDate | null;
  report_filters?: Array<SectionFilter>;
}

export interface SectionFilter {
  id?: string;
  key: string;
  field: string;
  label?: string;
  parent_key?: string;
  filter_type?: "string" | "number" | "option" | "date" | "boolean";
  sub_type?: string;
  type?: "single_select" | "multi_select";
  filter_on?: FilterOn;
  conditions?: Array<{
    operator: string;
    key?: string;
    parent_key?: string;
    label?: string;
    value:
      | string
      | string[]
      | number
      | { start_date: string; end_date: string; label: TimeInterval }
      | { min: number; max: number };
  }>;
  options?: Array<{
    key: string;
    label: string;
  }>;
  children?: Array<{
    id?: string;
    key: string;
    label?: string;
    filter_type?: "string" | "number" | "option" | "date";
    conditions?: Array<{
      operator: string;
      key?: string;
      parent_key?: string;
      label?: string;
      value:
        | string
        | number
        | string[]
        | { start_date: string; end_date: string; label: TimeInterval }
        | { min: number; max: number };
    }>;
    sub_type?: string;
    options?: Array<{
      key: string;
      label: string;
    }>;
    type?: "single_select" | "multi_select";
  }>;
  is_default?: boolean;
}

export interface ConfigurationAdditionalConfig {
  column_configuration?: {
    [key: string]: number;
  };
  sort_state?: {
    column: string | null;
    direction: "asc" | "desc" | false;
  };
  view_mode?:
    | "table_view"
    | "creative_view"
    | "bar_chart_view"
    | "line_chart_view"
    | "creative_share_view";
  graph_config?: {
    interval: "daily" | "weekly" | "monthly";
    metric?: string | null; // Deprecated: use metrics instead
    metrics?: string[]; // Array of metric keys
  };
}

export interface CreativeSectionConfiguration {
  date?: ReportDate;
  compare_date?: ReportDate | null;
  filters?: SectionFilter[];
  column_choices?: {
    [key: string]: string[];
  };
  dropdowns?: {
    [key: string]: string | string[] | null;
  };
  disabled_filters?: boolean;
  sectionId?: string;
  error?: string;
  operation_mode?: "update_only" | "fetch_only" | "both";
  additional_config?: ConfigurationAdditionalConfig;
  section_type?:
    | "fatigue"
    | "regular"
    | "fixer"
    | "dashboard_overview"
    | "dashboard_trends"
    | "dashboard_leaderboard"
    | "dashboard_new_creatives"
    | "dashboard_ad_account_overview";
  kpi_metric_id?: string;
}

export enum EAdAccountPlatform {
  FACEBOOK = "FACEBOOK",
  GOOGLE = "GOOGLE",
  LINKEDIN = "LINKEDIN",
  MICROSOFT = "MICROSOFT",
  TIKTOK = "TIKTOK",
}

export enum EAdAccountType {
  MANAGER = "MANAGER",
  CLIENT = "CLIENT",
}

export enum EAdAccountStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  NOT_CONNECTED = "NOT_CONNECTED",
  EXPIRED = "EXPIRED",
}

export interface WinningFilter {
  metric_id: string;
  conditions: WinningCondition[];
}
export type WinningCondition = {
  value: number | string;
  operator: WinningConditionOperator;
};
export enum WinningConditionOperator {
  LESS_THAN = "<",
  GREATER_THAN = ">",
  EQUAL_TO = "=",
}

export interface Logo {
  file_id: string;
  type?: string;
  url: string;
}
export interface IAccount {
  id: string;
  ad_account_id: string;
  status: EAdAccountStatus;
  name: string;
  type: EAdAccountType;
  creation_time_stamp: string;
  platform: EAdAccountPlatform;
  currency: string;
  is_demo?: boolean;
  additional_info?: {
    description?: string;
    instructions?: string;
    winning_filters: WinningFilter[];
    metric_ids: string[];
    default_filters?: SectionFilter[];
    attribution_windows?: string[];
    default_date?: AccountDefaultDateKey;
  };
  logos?: Logo[];
}

export enum LLMChoices {
  quality = "quality",
  fast = "fast",
}

export type SectionConfigWithMetadata = CreativeReportMetadata &
  CreativeSectionConfiguration;
